import React from "react";
import * as Sentry from "@sentry/react";
import {getBestName, getCategoryKey, getExternalID, RE_CATEGORY_PREFIXED_ID} from "./entityIds";
import {interpolate, interpolateEdgeDetails} from "../markdown/interpolation";
import { DEV } from "./configMgr";

// Throttle missing-dataset Sentry reports: collect unique dsKeys and flush once per second.
// Prevents N console.error + Sentry event calls when N entities share the same missing dataset.
// Each dsKey is reported once per page load: the result grid re-renders the same rows on every
// scroll, so a key missing for a whole session (a datasets.yml deploy lag, PLEX-6074) would
// otherwise be re-reported up to once a second while the affected rows keep re-rendering.
const _reportedDsKeys = new Set();
const _missingDsKeys = new Set();
let _missingDsFlushTimer = null;
const _reportMissingDataset = (dsKey) => {
  if (_reportedDsKeys.has(dsKey)) return;
  _reportedDsKeys.add(dsKey);
  _missingDsKeys.add(dsKey);
  if (!_missingDsFlushTimer) {
    _missingDsFlushTimer = setTimeout(() => {
      _missingDsFlushTimer = null;
      const keys = Array.from(_missingDsKeys);
      _missingDsKeys.clear();
      console.warn(`Dataset(s) missing from frontend config: ${keys.join(', ')}`);
      Sentry.captureMessage(`Missing frontend datasets: ${keys.join(', ')}`, {
        level: "warning",
        fingerprint: ["missing-frontend-datasets"],
        extra: { missingKeys: keys },
      });
    }, 1000);
  }
};

const splitID = (id) => {
  if (!id) return ["", ""];
  const colon = id.indexOf(':');
  return [id.substring(0, colon), id.substring(colon + 1)];
}
const DS_WIKIPEDIA = 'wikipedia';
const NO_WIKIPEDIA = /^(UCI|CID|S?CHEMBL|.{50,})/i;
// Map aliases' recognizable prefixes/patterns into dataset-native references
// Anything in an alias list matching a dataset's specified id_format pattern will transformed
const matchExternalID = (alias, datasets, category = null, baseCategory = null) => {
  const matches = [];
  datasets.forEach(ds => {
    const id_format = (ds.categories
        && (ds.categories[category] && ds.categories[category].id_format
          || ds.categories[baseCategory] && ds.categories[baseCategory].id_format))
      || ds.id_format;
    if (alias.startsWith(`${ds.id}:`)) {
      alias = alias.substring(ds.id.length + 1);
    }
    const [format, flags] = id_format.slice(-2) === "/i"
      ? [id_format.slice(0, -2), "i"]
      : [id_format, ""];
    try {
      const pattern = new RegExp(`^${format}$`, flags);
      if (pattern.test(alias)) {
        const replacement = `${ds.id_replace || "$1"}`;
        const externalID = alias.replace(pattern, replacement);
        if (externalID) {
          matches.push([ds, externalID]);
        }
      }
    } catch (error) {
      console.warn(`Bad dataset id format regular expression for '${ds.id}': "${ds.id_format}" (${error})`);
    }
  });
  return matches;
};
export const createSourceLink = (entity, datasets = {}, overrideExternalID = null, overrideDSKey = null, overrideCategory = null) => {
  // Publication nodes reached via entrezgene edges carry source_id=entrezgene:{gene_id},
  // which resolves to a gene bibliography page instead of the PubMed article.
  // Prefer the entity's own pubmed: id in that case.
  const isPubmedPublication = entity.category === 'publication' && entity.id?.startsWith('pubmed:');
  const primaryID = (!isPubmedPublication && entity.source_id) ? entity.source_id : entity.id;
  const [primaryDSKey, primaryExternalID] = splitID(primaryID) || splitID(entity.id);
  let externalID = overrideExternalID || primaryExternalID;
  const dsKey = overrideDSKey || primaryDSKey;
  // Empty {} is the searchSessionStore._datasets default before /api/datasets
  // resolves. Treat it the same as null — return silently rather than
  // logging a "Dataset missing for ds key 'X'" error per rendered entity.
  // The lazy entity.links getter (utils.js) re-runs once datasets are loaded
  // and link rendering catches up on the next observable tick.
  if (!datasets || Object.keys(datasets).length === 0) {
    return null;
  }
  const dataset = datasets[dsKey];
  if (!entity) {
    console.error("Entity not provided for source link", dataset, externalID);
    return null;
  }
  // Categories which support wikipedia links are identified in datasets.yml, wikipedia section
  if (!dataset) {
    if (dsKey) {
      // Batch reports: N entities with the same missing dataset key send one Sentry event, not N.
      _reportMissingDataset(dsKey);
    }
    return null;
  }
  if (!dataset.categories && dsKey !== "smiles" && dsKey !== "query") {
    if (dsKey !== DS_WIKIPEDIA) {
      DEV && console.warn(`No dataset categories found while resolving source link ${dsKey}:${externalID} (${entity.category})`, dataset);
    }
    return createWikipediaLink(entity, datasets, overrideExternalID);
  }
  const category = overrideCategory || entity.category;
  const baseCategory = overrideCategory || entity.baseCategory;

  // Look for dataset:<key>:dataset_id or <key>:<id> where key is a known category
  let info = null;
  if (RE_CATEGORY_PREFIXED_ID.test(externalID)) {
    const key = getCategoryKey(externalID);
    externalID = getExternalID(externalID);
    info = dataset.categories[key];
  } else if (/^[a-z][-a-z]+:\S/.test(externalID)) {
    const key = externalID.split(':')[0];
    if (dataset.categories && dataset.categories[key]) {
      externalID = externalID.slice(key.length + 1);
      info = dataset.categories[key];
    }
  }
  if (info === null) {
    info = dataset.categories[category] || dataset.categories[baseCategory];
  }
  if (!info || !info.url) {
    if (!dataset.name || !dataset.url) {
      console.warn(`No metadata found for dataset '${dataset.name}' category '${category}'`);
    }
    if (dsKey === "wikipedia") {
      // Not supported, or not likely to be found in wikipedia
      return null;
    }
    return {
      title: (info && info.name) || dataset.name,
      url: (info && info.url) || dataset.url,
      externalID, dataset,
    }
  }
  const props = {...entity};
  const eid = encodeURIComponent(externalID);
  const url = info.url
              ? interpolate(info.url, {...props, id: info.url.indexOf("urlEncode") === -1 ? eid : externalID}) : null;
  const url2 = info.url2 && (!info.pattern2 || new RegExp(info.pattern2, "g").test(externalID))
               ? interpolate(info.url2, {...props, id: info.url.indexOf("urlEncode") === -1 ? eid : externalID}) : null;
  return {
    title: info.name || dataset.name, url,
    description: dataset.description,
    title2: info.name2 ? info.name2 : null, url2,
    externalID,
    dataset: dsKey,
  }
};
const createWikipediaLink = (entity, datasets, term = null) => {
  // A target's Wikipedia page is found by its gene symbol (datasets.yml builds the URL from
  // {gene_symbol}). Its best name is its display title, with the full protein name and organism,
  // so NO_WIKIPEDIA's length rule dropped the link for titles of 50+ characters, e.g. PDE5A's
  // (PLEX-6678). A symbol is never an auto-generated chemical name, so it skips that check.
  const symbol = !term && (entity.category === "target" || entity.baseCategory === "target")
    ? entity.gene_symbol : null;
  const bestName = term || symbol || getBestName(entity);
  if (!bestName) {
    return null;
  }
  if (!symbol && NO_WIKIPEDIA.test(bestName)) {
    return null;
  }
  const dataset = datasets[DS_WIKIPEDIA];
  if (!(dataset.categories[entity.category] || dataset.categories[entity.baseCategory])) {
    return null;
  }
  const searchTerm = term || bestName.replace(' ', '_');
  return createSourceLink(entity, datasets, searchTerm, DS_WIKIPEDIA);
};


// generate "green" links
export const generateExternalLinks = (entity, datasets) => {
  // dataset:dataset_id; uses edge_url
  // dataset:<subtype>:dataset_id; uses <subtype>.url from 'categories' block of datasets.yml
  if (!datasets || Object.keys(datasets).length === 0) {
    // Empty {} is the searchSessionStore._datasets default before
    // /api/datasets resolves; treat the same as null. Don't warn —
    // the lazy entity.links getter re-runs once datasets are loaded.
    return [];
  }

  if (entity.aliases && entity.aliases.length > 10000) {
    console.warn(`Way too many aliases for ${entity.id} (${entity.aliases.length}), probably an error`)
    entity.aliases = entity.aliases.slice(0, 10000)
  }
  if (entity.unmapped || entity.id?.startsWith("smiles:")) {
    return [];
  }

  const linkURLs = new Set();
  const addLink = (links_array, link) => {
    if (!link || !links_array) {
      return null;
    }
    // avoid duplicate links
    if (!linkURLs.has(link.url)) {
      linkURLs.add(link.url);
      links_array.push(link);
    }
    if (link.title2 && link.url2 && !linkURLs.has(link.url2)) {
      linkURLs.add(link.url2);
      links_array.push({...link, title: link.title2, url: link.url2})
    }
    return link;
  };
  const links = [];
  const primaryLink = createSourceLink(entity, datasets);
  const allLinks = [];
  addLink(allLinks, primaryLink);
  const category = entity.category;
  const baseCategory = entity.baseCategory;
  try {
    const potentialLinks = (entity.aliases || []).slice();
    if (category === 'patent' && datasets.patents) {
      const [_, externalID] = splitID(entity.source_id || entity.id);
      potentialLinks.forEach(alias => {
        if (alias.startsWith(externalID)) {
          return;
        }
        // Only build a patents.google.com link for aliases that actually look like a
        // patent number (matches datasets.yml's patents id_format) — anything else
        // (titles, family IDs, etc.) would produce a broken link (PLEX-6182).
        matchExternalID(alias, [datasets.patents], category, baseCategory).forEach(([ds, matchedID]) => {
          addLink(allLinks, createSourceLink(entity, datasets, matchedID, ds.id));
        });
      });
    }
    if (entity.title) {
      potentialLinks.push(entity.title);
    }
    if (entity.source_id) {
      potentialLinks.push(entity.source_id);
    }
    if (entity.ref_id) {
      potentialLinks.push(entity.ref_id);
    }
    if (primaryLink?.title !== datasets.wikipedia?.name) {
      // Include a wikipedia link where appropriate
      addLink(allLinks, createWikipediaLink(entity, datasets));
    }
    potentialLinks.forEach(alias => {
      const [cat, baseCat] = alias === entity.ref_id ? [entity.ref_category, entity.ref_category] : [category, baseCategory];
      const filteredDatasets = Object.values(datasets)
        // Guard `v` and `v.categories`: a dataset may carry a top-level
        // `id_format` with no `categories` block (see matchExternalID's
        // id_format fallback and the !dataset.categories path in
        // createSourceLink). Without the guard, `v.categories[cat]` throws
        // "Cannot read properties of undefined (reading '<cat>')" — the whole
        // entity then bails to the catch below and loses ALL its links
        // (WEBAPP-95 / PLEX-5934).
        .filter(v => v && v.id_format && (v.categories?.[cat] || v.categories?.[baseCat]))
        // Omit google links for schembl-only patents
        .filter(v => entity.source !== "schembl" && v.id !== "patents");
      const matches = matchExternalID(alias, filteredDatasets, cat, baseCat);
      matches.forEach(match => {
        const [ds, externalID] = match;
        if (ds.id !== DS_WIKIPEDIA) {
          // May want to filter out pubchem/schembl patent "aliases"
          addLink(allLinks, createSourceLink(entity, datasets, externalID, ds.id, baseCat));
        }
      });
    });
    return allLinks;
  } catch (error) {
    console.error(`Error generating external links for ${entity.id}`, error);
    return links;
  }
};

// Limited markdown rendering: generate links from markdown links
export const generateLinksFromMarkdown = ({text = "", referencedEdges = {}, asString = false}) => {
  const s = text;
  let lastEndIndex = 0;
  const RE = /\[([^\]]+)]\(([^)]+)\)/g;
  const parts = [];
  let match;
  let key = 0;
  while ((match = RE.exec(s)) != null) {
    const unmatched = s.substring(lastEndIndex, match.index);
    if (unmatched !== "") {
      parts.push(asString ? unmatched : (<span key={++key}>{unmatched}</span>));
    }
    parts.push(
      asString ? `[${match[1]}](${match[2]})` : (<a key={++key} href={match[2]} target="_blank">{match[1]}</a>));
    lastEndIndex = match.index + match[0].length;
  }
  if (lastEndIndex < s.length) {
    parts.push(asString ? s.substring(lastEndIndex) : (<span key={++key}>{s.substring(lastEndIndex)}</span>));
    if (asString) {
      parts.push("\n");
      Object.entries(referencedEdges).forEach(([id, url]) => {
        parts.push(`[${id}]: ${url}\n`)
      });
    }
  }
  return asString ? parts.join("") : parts;
};

// An evidence record's ID ("<dataset>:<record>", e.g. opentargets:ENSG00000141510/EFO_0000305)
// names no entity, so resolving it as one fails, and the link it was written as broke (PLEX-6500).
// Link it the way its dataset links its evidence, through edge_url, when the record part alone fills
// the template. Returns {name, url}: url is null when the template needs a field the ID doesn't carry
// (gwas-catalog's {strongest_rsid}); null when the prefix isn't a dataset with evidence links.
export const evidenceLink = (id, datasets) => {
  const match = typeof id === "string" && id.match(/^([a-z][-_a-z0-9]*):(.+)$/);
  const dataset = match && datasets?.[match[1]];
  if (!dataset?.edge_url) {
    return null;
  }
  const name = `${dataset.name || match[1]} evidence`;
  const fields = [...dataset.edge_url.matchAll(/\{([^}]*)\}/g)].map(m => m[1]);
  if (fields.some(f => f !== "id")) {
    return {name, url: null};
  }
  let url = null;
  try {
    url = interpolateEdgeDetails({dataset_id: match[2]}, dataset, null, null, datasets).url;
  } catch (e) {
    url = null;
  }
  return {name, url: typeof url === "string" && /^https?:\/\/[^\s{}]+$/.test(url) ? url : null};
};
